import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import {
  CameraIcon,
  CrossCircledIcon,
  DotFilledIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { Container, Drink } from "src/lib/db";
import { useGetDrinkBySelection } from "src/lib/hooks/useGetDrinkById";
import resizeImageFromDataUrl from "src/lib/resizeImage";
import { addDrink, calculatePermille } from "src/lib/slices/alcoholSlice";
import { setDrinkUsed } from "src/lib/slices/drinkCache";
import {
  selectDefaultContainer,
  setDefaultContainer,
} from "src/lib/slices/userSlice";
import { v4 as uuidv4 } from "uuid";
import { getSelectedContainer } from "../../lib/slices/drinkSelectionSlice";
import AddInPastButton from "../addInPastButton";
import DrinkImage from "../DrinkImage";
import Image from "../Image";
import PhotoPreview from "../PhotoPreview";
import { Button, ButtonStack } from "../ui/button";
import ContainerDrawer from "./ContainerDrawer";
import DrinkDrawer from "./DrinkDrawer";
import styles from "./DrinkSelector.module.css";

export default function DrinkSelector() {
  const dispatch = useDispatch();

  const locationState = useSelector((state: any) => state.location);

  const photoRef = useRef<HTMLInputElement>(null);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [setAnimationClasses, setSetAnimationClasses] = useState<boolean>(null);

  const selection = useSelector((state: any) => state.drinkSelection);

  const user = useSelector((state: any) => state.user);

  const selectedContainer: Container | undefined =
    getSelectedContainer(selection);

  const selectedDrink: Drink | undefined = useGetDrinkBySelection(selection);

  const [containerSelectionOpen, setContainerSelectionOpen] = useState(false);
  const [drinkSelectionOpen, setDrinkSelectionOpen] = useState(false);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  const drinkAvailable = Boolean(selectedDrink) && Boolean(selectedContainer);

  const defaultContainer = useSelector(({ user }: any) =>
    selectDefaultContainer({
      drinkId: selection.drinkId,
      containerId: selectedDrink?.defaultContainerId,
    })(user)
  );

  const getLatLong = () => {
    const lastLocationUpdate = locationState.lastUpdate;

    if (new Date().getTime() - lastLocationUpdate < 10 * 60 * 1000) {
      return {
        latitude: locationState.latitude,
        longitude: locationState.longitude,
      };
    }

    return {};
  };

  const drinkDrink = ({ date }: { date?: number }) => {
    if (selectedDrink.icon?.startsWith("data:")) {
      toast(
        "Obrázek nápoje ještě nebyl nahrán na cloud, zkus to prosím později."
      );
      return;
    }
    setSetAnimationClasses(false);

    const { latitude, longitude } = getLatLong();

    dispatch(
      addDrink({
        drink: selectedDrink,
        container: selectedContainer,
        photoData,
        latitude,
        longitude,
        date: date ?? new Date().getTime(),
        id: uuidv4(),
      })
    );
    if (
      defaultContainer !== selection.containerId &&
      selection.containerId !== "Own"
    ) {
      toast(`Nastavit jako výchozí nádobu na ${selectedDrink.name}?`, {
        description: `${selectedContainer.name} (${selectedContainer.volume} ${selectedContainer.units})`,
        action: {
          label: "Ano",
          onClick: () => {
            dispatch(setDefaultContainer(selection));
          },
        },
      });
    }
    dispatch(setDrinkUsed({ id: selection.drinkId }));
    dispatch(calculatePermille({ user }));
    setPhotoData(null);

    if (Capacitor.isNativePlatform())
      (async () => {
        Haptics.impact({ style: ImpactStyle.Light });
        _.delay(() => Haptics.impact({ style: ImpactStyle.Medium }), 250);
        _.delay(() => Haptics.impact({ style: ImpactStyle.Heavy }), 500);
      })();
  };

  useEffect(() => {
    if (setAnimationClasses === false) setSetAnimationClasses(true);
  }, [setAnimationClasses]);

  return (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          alignItems: "end",
          marginBottom: "6rem",
        }}
      >
        <div
          style={{
            position: "absolute",
            backgroundColor: "hsl(var(--muted))",
            height: "6rem",
            bottom: "-5rem",
            width: "100%",
            zIndex: -1,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-6rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <input
            type="file"
            accept="image/png, image/jpeg"
            name="photo"
            id="photo"
            ref={photoRef}
            style={{ display: "none" }}
            onChange={(event) => {
              event.preventDefault();
              const { files } = event.target;
              if (!files?.[0]) return;
              const reader = new FileReader();
              reader.onloadend = async () => {
                const dataUrl = await resizeImageFromDataUrl({
                  dataUrl: reader.result as string,
                });
                if (!dataUrl) return;
                setPhotoData(dataUrl);
              };
              reader.readAsDataURL(files[0]);
            }}
          />
          {photoData && (
            <Button
              size="icon"
              variant="destructive"
              onClick={() => {
                setPhotoData(null);
              }}
            >
              <CameraIcon />
              <CrossCircledIcon />
            </Button>
          )}
          {!photoData && (
            <Button
              size="icon"
              className="absolute right-4"
              onClick={() => {
                if (Capacitor.isNativePlatform()) {
                  Camera.getPhoto({
                    quality: 100,
                    allowEditing: false,
                    resultType: CameraResultType.DataUrl,
                    source: CameraSource.Camera,
                  })
                    .then(async (photo) => {
                      const dataUrl = await resizeImageFromDataUrl({
                        dataUrl: photo.dataUrl as string,
                      });
                      setPhotoData(dataUrl);
                    })
                    .catch(() => {});
                } else {
                  photoRef.current?.click();
                }
              }}
            >
              <CameraIcon />
              <PlusIcon />
            </Button>
          )}

          <ButtonStack disabled={!drinkAvailable}>
            <AddInPastButton
              actionLabel="Vypít"
              onSubmit={drinkDrink}
              disabled={!drinkAvailable}
            />
            <Button
              stack="right"
              onClick={() => drinkDrink({})}
              disabled={!drinkAvailable}
            >
              Vypít
            </Button>
          </ButtonStack>

          {photoData && (
            <Image
              style={{
                position: "absolute",
                right: "1rem",
                height: "24vw",
                width: "16vw",
                objectFit: "cover",
                rotate: "25deg",
                cursor: "pointer",
                borderRadius: "0.5rem",
              }}
              src={photoData}
              alt="Fotka uživatele"
              onClick={() => setPhotoPreviewOpen(true)}
            />
          )}
        </div>
        <div
          style={{
            position: "relative",
            flex: 2,
            marginLeft: "1rem",
            cursor: "pointer",
          }}
          onClick={() => setContainerSelectionOpen(true)}
        >
          {selectedContainer ? (
            <>
              <img
                src={`/container-images/${
                  selectedContainer.icon ?? "shot.svg"
                }`}
                alt={selectedContainer.name}
                style={{
                  height: `calc(5rem * ${
                    selectedContainer.iconSizeMultiplier ?? 1
                  })`,
                  margin: "0 auto",
                }}
                className={setAnimationClasses ? styles.container : undefined}
              />
              <div
                style={{
                  textAlign: "center",
                  position: "absolute",
                  width: "100%",
                  paddingTop: "0.5rem",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  lineHeight: "1rem",
                }}
              >
                {selectedContainer.name}
                <br />
                <span style={{ fontWeight: "400", fontSize: "0.75rem" }}>
                  {selectedContainer.volume} {selectedContainer.units}
                </span>
              </div>
            </>
          ) : (
            <div
              className="flex flex-col items-center justify-end"
              style={{ minHeight: "5rem" }}
            >
              {selectedDrink && (
                <>
                  <DotFilledIcon
                    className="animate-ping"
                    width={50}
                    height={50}
                  />
                  <div className="hint-text pt-2 text-center max-w-28">
                    Zde vyber kolik piješ
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            position: "relative",
            flex: 3,
            marginRight: "1rem",
            cursor: "pointer",
          }}
          onClick={() => setDrinkSelectionOpen(true)}
        >
          {selectedDrink ? (
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className={setAnimationClasses ? styles.drink : undefined}>
                  <DrinkImage
                    drink={selectedDrink}
                    alt={selectedDrink.name}
                    style={{
                      height: "10rem",
                      margin: "0 auto",
                      maxWidth: "10rem",
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  position: "absolute",
                  width: "100%",
                  paddingTop: "0.5rem",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  lineHeight: "1rem",
                }}
              >
                {selectedDrink.name}
                <br />
                <span style={{ fontWeight: "400", fontSize: "0.75rem" }}>
                  {selectedDrink.alcoholVolume} %
                </span>
              </div>
            </>
          ) : (
            <div
              className="flex flex-col items-center justify-center"
              style={{ minHeight: "10rem" }}
            >
              <DotFilledIcon className="animate-ping" width={50} height={50} />
              <div className="hint-text pt-2 text-center max-w-28">
                Zde vyber co piješ
              </div>
            </div>
          )}
        </div>
      </div>
      <ContainerDrawer
        containerSelectionOpen={containerSelectionOpen}
        setContainerSelectionOpen={setContainerSelectionOpen}
        selection={selection}
      />
      <DrinkDrawer
        drinkSelectionOpen={drinkSelectionOpen}
        setDrinkSelectionOpen={setDrinkSelectionOpen}
        selection={selection}
        selectedDrink={selectedDrink}
      />
      <PhotoPreview
        setOpen={setPhotoPreviewOpen}
        open={photoPreviewOpen}
        imgUrl={photoData}
        alt="Fotka uživatele"
      />
    </>
  );
}
